import { useFormContext } from 'react-hook-form';
import { Input, Country, PostCode } from '@/components/fields';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

export default function Address({
  id,
  tenancyPeriods,
  setTenancyPeriods,
  previousAddresses,
  setPreviousAddresses,
  isAddressShown = false,
}: {
  id: number;
  tenancyPeriods: {
    [key: string]: number;
  };
  setTenancyPeriods: Dispatch<
    SetStateAction<{
      [key: string]: number;
    }>
  >;
  previousAddresses: number;
  setPreviousAddresses: Dispatch<SetStateAction<number>>;
  isAddressShown?: boolean;
}): JSX.Element {
  const [showAddress, setShowAddress] = useState(false);
  const [manualAddress, setManualAddress] = useState(false);
  const { errors, watch, unregister, clearErrors } = useFormContext();
  const yearsLived = watch(`previous_address_${id}_years_lived`);
  const monthsLived = watch(`previous_address_${id}_months_lived`);
  const totalMonths =
    parseInt(yearsLived || 0) * 12 + parseInt(monthsLived || 0);
  const displayAddress = showAddress || manualAddress || isAddressShown;
  const addressFields = [
    `postcode_lookup_previous_address_${id}`,
    `previous_address_${id}_building_name_number`,
    `previous_address_${id}_street_name`,
    `previous_address_${id}_address_line_2`,
    `previous_address_${id}_town`,
    `previous_address_${id}_postcode`,
    `previous_address_${id}_country`,
    `previous_address_${id}_years_lived`,
    `previous_address_${id}_months_lived`,
  ];

  const removeAddress = () => {
    setTenancyPeriods((prevPeriods) => {
      const newPeriods = { ...prevPeriods };
      delete newPeriods[id];

      return newPeriods;
    });

    if (previousAddresses === 1) {
      setPreviousAddresses(0);
    } else {
      setPreviousAddresses((value) => value - 1);
    }

    unregister(addressFields);
    clearErrors(addressFields);
  };

  useEffect(() => {
    setTenancyPeriods({ ...tenancyPeriods, [id]: totalMonths });
  }, [totalMonths]);

  return (
    <>
      <h4 className="text-xl leading-6 font-medium tracking-tight text-gray-900 flex items-center mb-5 mt-10">
        Previous Address {id}
      </h4>
      <PostCode
        id={`postcode_lookup_previous_address_${id}`}
        isRequired={false}
        setShowAddress={setShowAddress}
        setManualAddress={setManualAddress}
      >
        Address Postcode
      </PostCode>
      <div className="flex">
        <div className="md:w-2/5 md:pr-4"></div>
        <div className="md:w-2/5 md:pl-4">
          {errors?.[`previous_address_${id}_postcode`] && (
            <p className="-mt-2 text-sm text-red-600">
              {errors[`previous_address_${id}_postcode`].message}
            </p>
          )}
        </div>
      </div>
      <div className={displayAddress ? 'block' : 'hidden'}>
        <div className="hidden">
          <Input id={`previous_address_${id}_building_name_number`} isRequired>
            Building Name/Number
          </Input>
        </div>
        <Input id={`previous_address_${id}_street_name`} isRequired>
          Address line 1
        </Input>
        <Input id={`previous_address_${id}_address_line_2`} isRequired={false}>
          Address line 2
        </Input>
        <Input id={`previous_address_${id}_town`} isRequired>
          Town
        </Input>
        <Input id={`previous_address_${id}_postcode`} isRequired>
          Postcode
        </Input>
        <Country id={`previous_address_${id}_country`} isRequired>
          Country
        </Country>
      </div>
      <div className="mt-4 md:flex md:items-center">
        <div className="md:w-2/5 md:pr-4">
          <p className="text-sm leading-5 font-normal text-gray-900">
            How long have you lived at this address?{' '}
            <sup className="text-red-600 font-sans">*</sup>
            <br />
            <span className="font-medium text-xs text-red-600">
              Tenancy years must be more than 3 years
            </span>
          </p>
        </div>
        <div className="md:w-2/5 md:pl-4">
          <div className="flex justify-between">
            <div className="w-1/2 pr-2">
              <Input
                id={`previous_address_${id}_years_lived`}
                isRequired
                position="right"
              >
                Years
              </Input>
            </div>
            <div className="w-1/2 pl-2">
              <Input
                id={`previous_address_${id}_months_lived`}
                isRequired
                position="right"
              >
                Months
              </Input>
              {previousAddresses === id && (
                <button
                  className="mb-3 ml-auto flex justify-end font-semibold text-xs bg-gray-800 text-white px-2 rounded-md"
                  onClick={removeAddress}
                >
                  <span className="text-red-600 pr-2">x</span> remove address
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
